'use client';

// ============================================================================
// CrewAhead Maritime Platform - useMatching Hook
// ============================================================================
// Wraps the matching API with TanStack Query caching.
// Provides match scores for candidates (matched jobs) and employers
// (matched candidates), plus detailed score breakdowns.

import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getMatchedJobs as getMatchedJobsApi,
  getMatchScore as getMatchScoreApi,
  getMatchedCandidates as getMatchedCandidatesApi,
} from '../lib/api';
import type { CrewAheadApiError } from '../lib/api/client';
import { QUERY_KEYS } from '../lib/types/api';
import type { MatchedJob, MatchedCandidate, MatchScoreDetail } from '../lib/types';
import { useAuthStore } from '../store';
import { selectIsSeafarer, selectIsEmployer, selectIsShoreStaff } from '../store';

// ─── Hook Return Type ────────────────────────────────────────────────────────

export interface UseMatchingReturn {
  // ─── Matched Jobs (candidate view) ────────────────────────────────────────
  matchedJobs: MatchedJob[];
  totalMatched: number;
  isMatchedJobsLoading: boolean;
  matchedJobsError: CrewAheadApiError | null;
  refetchMatchedJobs: () => Promise<void>;

  // ─── Match Score for a Specific Job ───────────────────────────────────────
  matchScore: MatchScoreDetail | null;
  isMatchScoreLoading: boolean;

  // ─── Matched Candidates (employer view) ───────────────────────────────────
  matchedCandidates: MatchedCandidate[];
  isCandidatesLoading: boolean;

  // ─── Filters ──────────────────────────────────────────────────────────────
  minScore: number;
  setMinScore: (score: number) => void;
}

// ─── useMatching Hook ────────────────────────────────────────────────────────

/**
 * Hook for job-to-candidate and candidate-to-job matching.
 *
 * Provides three independent queries:
 * 1. `matchedJobs` — for seafarers/shore staff, jobs ranked by match score
 * 2. `matchScore` — detailed breakdown for a single job (when jobId provided)
 * 3. `matchedCandidates` — for employers, candidates ranked for a job
 *
 * Queries are enabled only when relevant to the user's role and options.
 * Match scores are cached for 5 minutes (they don't change that often).
 *
 * @param options — Job ID for score/candidates, min score threshold, page
 *
 * @example
 * ```tsx
 * // Candidate: see matched jobs
 * const { matchedJobs, isMatchedJobsLoading } = useMatching();
 *
 * // Candidate: see score for one job
 * const { matchScore } = useMatching({ jobId: '123' });
 *
 * // Employer: see candidates for a job
 * const { matchedCandidates } = useMatching({ jobId: '456' });
 * ```
 */
export function useMatching(options?: {
  jobId?: string;
  minScore?: number;
  page?: number;
}): UseMatchingReturn {
  // ─── Role Checks ───────────────────────────────────────────────────────────

  const isSeafarer = useAuthStore(selectIsSeafarer);
  const isShoreStaff = useAuthStore(selectIsShoreStaff);
  const isEmployer = useAuthStore(selectIsEmployer);

  const isCandidate = isSeafarer || isShoreStaff;

  // ─── Local State ───────────────────────────────────────────────────────────

  const [minScore, setMinScore] = useState<number>(options?.minScore ?? 50);

  // ─── Matched Jobs Query (candidate view) ───────────────────────────────────

  const {
    data: matchedJobsData,
    isLoading: isMatchedJobsLoading,
    error: matchedJobsError,
    refetch: refetchMatchedJobsQuery,
  } = useQuery({
    queryKey: QUERY_KEYS.MATCHING.JOBS_FILTERED({
      minScore,
      page: options?.page ?? 1,
    }),
    queryFn: () =>
      getMatchedJobsApi({
        minScore,
        page: options?.page,
      }),
    enabled: isCandidate,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // ─── Match Score Query (single job) ────────────────────────────────────────

  const {
    data: matchScoreData,
    isLoading: isMatchScoreLoading,
  } = useQuery({
    queryKey: options?.jobId
      ? QUERY_KEYS.MATCHING.SCORE(options.jobId)
      : ['matching', 'score', 'none'],
    queryFn: () => {
      if (!options?.jobId) throw new Error('No job ID provided');
      return getMatchScoreApi(options.jobId);
    },
    enabled: !!options?.jobId,
    staleTime: 1000 * 60 * 5,
  });

  // ─── Matched Candidates Query (employer view) ──────────────────────────────

  const {
    data: matchedCandidatesData,
    isLoading: isCandidatesLoading,
  } = useQuery({
    queryKey: options?.jobId
      ? QUERY_KEYS.MATCHING.CANDIDATES(options.jobId)
      : ['matching', 'candidates', 'none'],
    queryFn: () => {
      if (!options?.jobId) throw new Error('No job ID provided');
      return getMatchedCandidatesApi(options.jobId, { minScore });
    },
    enabled: isEmployer && !!options?.jobId,
    staleTime: 1000 * 60 * 5,
  });

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const refetchMatchedJobs = useCallback(async (): Promise<void> => {
    await refetchMatchedJobsQuery();
  }, [refetchMatchedJobsQuery]);

  // ─── Derived Data ──────────────────────────────────────────────────────────

  const matchedJobs = useMemo(
    () => matchedJobsData?.results ?? [],
    [matchedJobsData]
  );

  const totalMatched = useMemo(
    () => matchedJobsData?.count ?? 0,
    [matchedJobsData]
  );

  const matchScore = useMemo(
    () => matchScoreData ?? null,
    [matchScoreData]
  );

  const matchedCandidates = useMemo(
    () => matchedCandidatesData?.results ?? [],
    [matchedCandidatesData]
  );

  return {
    // Matched jobs
    matchedJobs,
    totalMatched,
    isMatchedJobsLoading,
    matchedJobsError: (matchedJobsError as CrewAheadApiError | null) ?? null,
    refetchMatchedJobs,

    // Match score
    matchScore,
    isMatchScoreLoading,

    // Matched candidates
    matchedCandidates,
    isCandidatesLoading,

    // Filters
    minScore,
    setMinScore,
  };
}
